import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  getFilteredBuildingGroups,
  performBuildingGroupsMassAction,
  removeBuildingFromBuildingGroups,
} from '../../../../api'
import { PAGINATION_ALL_ITEMS_VALUE } from '../../../../constants/pagination'
import {
  useMassActions,
  useNotification,
  usePerformMassAction,
} from '../../../../hooks'
import { getItemsFromIdwellApiResponse } from '../../../../utils/api'
import { getItemsIds } from '../../../../utils/common'
import { getPaginationApiParams } from '../../../../utils/pagination'
import { FORMIK_ENTITY_ACTION_CONFIG_FIELDS_TYPE_SELECT_NAME } from '../../../Formik'
import {
  BUILDING_GROUP_TABLE_MASS_ACTION_NAMES,
  BUILDING_GROUPS_ACTION_DELETE_FROM_GROUP,
  BUILDING_GROUPS_ACTION_DELETE_FROM_GROUP_MASS,
  BUILDING_GROUPS_ACTION_EDIT_BUILDINGS,
  BUILDING_GROUPS_ACTION_EDIT_BUILDINGS_MASS,
  BUILDING_GROUPS_ACTION_EDIT_MANAGERS,
  BUILDING_GROUPS_ACTION_EDIT_MANAGERS_MASS,
  BUILDING_GROUPS_ACTION_REMOVE,
  BUILDING_GROUPS_ACTION_REMOVE_MASS,
} from '../../BuildingGroup.constants'
import {
  getBuildingGroupTableAsyncNotificationText,
  getBuildingGroupTableItemActions,
  getBuildingGroupTableNotificationTitle,
} from '../../BuildingGroup.utils'
import {
  BuildingGroupsEditBuildingsActionModal,
  BuildingGroupsEditManagersActionModal,
  BuildingGroupsRemoveActionModal,
} from '../../BuildingGroupActions'

import {
  getBuildingGroupDeleteFromGroupPayload,
  getItemActionTypes,
} from './useBuildingGroupTableActions.utils'

export function useBuildingGroupTableActions({
  excludeIds,
  filterValues,
  pagination,
  scopeBuildingId,
  selectAllPages,
  selectedIds,
  sub,
  onCleanSelection,
  onCloseModal,
  onReload,
  onSetModal,
}) {
  const { t } = useTranslation()

  const [isRemovingFromGroup, setRemovingFromGroup] = useState(false)

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const {
    getAsyncTaskId,
    handleAddAsyncMassActionNotification,
    handleAddFinishMassActionNotification,
  } = useMassActions({
    getAsyncNotificationText: getBuildingGroupTableAsyncNotificationText,
    getNotificationTitle: getBuildingGroupTableNotificationTitle,
    onSuccessHandler: onReload,
  })

  function handleErrorAction() {
    onCloseModal()

    handleAddErrorNotification('Notification.BuildingGroupsMassActionErrorKey')
  }

  const { isPerformingMassAction, massAction } = usePerformMassAction({
    excludeIds,
    filterValues,
    getAsyncTaskId,
    massActionNames: BUILDING_GROUP_TABLE_MASS_ACTION_NAMES,
    pagination,
    performApi: performBuildingGroupsMassAction,
    selectAllPages,
    selectedIds,
    sub,
    onAddAsyncMassActionNotification: handleAddAsyncMassActionNotification,
    onAddFinishMassActionNotification: handleAddFinishMassActionNotification,
    onCleanSelection,
    onCloseModal,
    onErrorAction: handleErrorAction,
    onReload,
  })

  function getItemActions(item) {
    return getBuildingGroupTableItemActions({
      item,
      withDeleteFromGroup: Boolean(scopeBuildingId),
    })
  }

  function getHandleSubmitRemove({ action, id }) {
    return ({ values }) => {
      massAction({
        ...(id ? { ids: [id] } : null),
        action,
        value: values,
      })
    }
  }

  function showConfirmDeleteModal({ action, id }) {
    onSetModal(
      <BuildingGroupsRemoveActionModal
        onCloseModal={onCloseModal}
        onSubmit={getHandleSubmitRemove({
          action,
          id,
        })}
      />
    )
  }

  // Resolves the "select all pages" selection through the list endpoint and
  // the table filters, so filtered-out groups are not touched.
  async function getFilteredBuildingGroupIds() {
    const response = await getFilteredBuildingGroups({
      ...getPaginationApiParams(PAGINATION_ALL_ITEMS_VALUE),
      filters: filterValues,
    })

    return getItemsIds(getItemsFromIdwellApiResponse(response))
  }

  async function getBuildingGroupIdsToRemove(id) {
    if (Number.isFinite(id)) {
      return [id]
    }

    if (!selectAllPages) {
      return selectedIds
    }

    const filteredBuildingGroupIds = await getFilteredBuildingGroupIds()

    return filteredBuildingGroupIds.filter(
      groupId => !excludeIds.includes(groupId)
    )
  }

  async function removeFromGroup({ action, id, values }) {
    const buildingGroupIds = await getBuildingGroupIdsToRemove(id)

    await removeBuildingFromBuildingGroups({
      data: getBuildingGroupDeleteFromGroupPayload({
        buildingGroupIds,
        values,
      }),
      id: scopeBuildingId,
    })

    onCleanSelection()
    onReload()

    handleAddSuccessNotification({
      title: getBuildingGroupTableNotificationTitle({
        action,
        count: buildingGroupIds.length,
        t,
        totalCount: buildingGroupIds.length,
      }),
    })
  }

  // The building-groups mass action has no "remove" command, so delete-from-group
  // goes through the dedicated building endpoint instead of `massAction`.
  function getHandleSubmitDeleteFromGroup({ action, id }) {
    return ({ values }) => {
      onCloseModal()

      setRemovingFromGroup(true)

      removeFromGroup({ action, id, values })
        .catch(handleErrorAction)
        .finally(() => {
          setRemovingFromGroup(false)
        })
    }
  }

  function showDeleteFromGroupModal({ action, id }) {
    onSetModal(
      <BuildingGroupsRemoveActionModal
        confirmationTextKey='Modal.DeleteFromBuildingGroupConfirmationTextKey'
        formTitle='Modal.DeleteFromBuildingGroupTitleKey'
        onCloseModal={onCloseModal}
        onSubmit={getHandleSubmitDeleteFromGroup({
          action,
          id,
        })}
      />
    )
  }

  function getHandleSubmitActionWithType({ action, id }) {
    return ({ payload, values }) => {
      massAction({
        ...(id ? { ids: [id] } : null),
        action,
        actionType: values[FORMIK_ENTITY_ACTION_CONFIG_FIELDS_TYPE_SELECT_NAME],
        value: payload,
      })
    }
  }

  function showEditBuildingsModal({ action, id }) {
    onSetModal(
      <BuildingGroupsEditBuildingsActionModal
        formFieldsProps={{
          actionTypes: Number.isFinite(id) ? getItemActionTypes() : undefined,
        }}
        id={id}
        itemAction={Number.isFinite(id)}
        onClose={onCloseModal}
        onSubmit={getHandleSubmitActionWithType({ action, id })}
      />
    )
  }

  function showEditManagersModal({ action, id, users }) {
    onSetModal(
      <BuildingGroupsEditManagersActionModal
        formFieldsProps={{
          actionTypes: Number.isFinite(id) ? getItemActionTypes() : undefined,
        }}
        itemAction={Number.isFinite(id)}
        users={users}
        onClose={onCloseModal}
        onSubmit={getHandleSubmitActionWithType({ action, id })}
      />
    )
  }

  function setItemAction(action, item) {
    switch (action) {
      case BUILDING_GROUPS_ACTION_REMOVE:
        showConfirmDeleteModal({
          action: BUILDING_GROUPS_ACTION_REMOVE,
          id: item.id,
        })
        break
      case BUILDING_GROUPS_ACTION_EDIT_MANAGERS:
        showEditManagersModal({
          action: BUILDING_GROUPS_ACTION_EDIT_MANAGERS,
          id: item.id,
          users: item.responsible_users,
        })
        break
      case BUILDING_GROUPS_ACTION_EDIT_BUILDINGS:
        showEditBuildingsModal({
          action: BUILDING_GROUPS_ACTION_EDIT_BUILDINGS,
          id: item.id,
        })
        break
      case BUILDING_GROUPS_ACTION_DELETE_FROM_GROUP:
        showDeleteFromGroupModal({
          action: BUILDING_GROUPS_ACTION_DELETE_FROM_GROUP,
          id: item.id,
        })
        break
      default:
        return null
    }
  }

  function setMassAction(action) {
    switch (action) {
      case BUILDING_GROUPS_ACTION_REMOVE_MASS:
        showConfirmDeleteModal({
          action: BUILDING_GROUPS_ACTION_REMOVE_MASS,
        })
        break
      case BUILDING_GROUPS_ACTION_EDIT_MANAGERS_MASS:
        showEditManagersModal({
          action: BUILDING_GROUPS_ACTION_EDIT_MANAGERS_MASS,
        })
        break
      case BUILDING_GROUPS_ACTION_EDIT_BUILDINGS_MASS:
        showEditBuildingsModal({
          action: BUILDING_GROUPS_ACTION_EDIT_BUILDINGS_MASS,
        })
        break
      case BUILDING_GROUPS_ACTION_DELETE_FROM_GROUP_MASS:
        showDeleteFromGroupModal({
          action: BUILDING_GROUPS_ACTION_DELETE_FROM_GROUP_MASS,
        })
        break
      default:
        return null
    }
  }

  return {
    getItemActions,
    isPerformingMassAction: isPerformingMassAction || isRemovingFromGroup,
    setItemAction,
    setMassAction,
  }
}
